import { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { getDays, getMonths, isValidDay, getBirthdayYearOptions } from 'utils/date';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { AgentUserQuery } from 'interfaceAdapter/queries/AgentUser';
import { AgentUserRepository } from 'interfaceAdapter/repositories/AgentUser';
import { useNavigate } from 'react-router-dom';
import { useLoading } from 'application/contexts/useLoading';
import { agentUserContext } from 'application/contexts/useAgentUser';

export const schema = yup.object().shape({
    first_name: yup.string().required().max(50),
    last_name: yup.string().required().max(50),
    first_name_kana: yup.string().required().max(50),
    last_name_kana: yup.string().required().max(50),
    icon_image: yup.string().nullable().defined(),
    department: yup.string().nullable().defined(),
    job: yup.string().nullable().defined(),
    gender: yup.number().required(),
    phone_number: yup.string().required().max(30).onlyNumber().defined(),
    year: yup.number().required(),
    month: yup.number().required(),
    day: yup.number().required(),
});

type AgentUserForm = yup.InferType<typeof schema>;

export const useAgentUserProfileRegistration = () => {
    const diContainer = useContext(diContainerContext);
    const agentUserQuery = diContainer.resolve(AgentUserQuery);
    const agentUserRepository = diContainer.resolve(AgentUserRepository);
    const { loading, setLoading } = useLoading();
    const [showSnackbar, setShowSnackbar] = useState(false);
    const { notCompletedProfile } = useContext(agentUserContext);
    const navigate = useNavigate();

    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        watch,
        setValue,
        setError,
        clearErrors,
    } = useForm<AgentUserForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            icon_image: null,
            year: 1920,
            month: 1,
            day: 1,
            job: '',
            department: '',
        },
    });

    useEffect(() => {
        const checkCanEnter = async () => {
            const { status } = await agentUserQuery.checkToken(true);

            if (status === false) {
                // 有効なトークンが無いのでリダイレクト
                navigate('/agent/login/');
            }
        };
        checkCanEnter();
    }, [window.location]);

    const submit = handleSubmit(async (data) => {
        setLoading(true);
        try {
            const birthday = `${data.year}-${data.month}-${data.day}`;

            await agentUserRepository.registerProfile({
                ...data,
                birthday,
            });
            setShowSnackbar(true);
            // TODO 確認画面の実装をサボっている
            setTimeout(() => window.location.replace('/agent/dashboard/'), 1500);
        } finally {
            setLoading(false);
        }
    });

    useEffect(() => {
        // 生年月日の組み合わせに対するバリデーション
        const subscription = watch((value, { name }) => {
            if (!['year', 'month', 'day'].includes(name ?? '')) {
                return;
            }
            const { year = -1, month = -1, day = -1 } = value;
            if (value.year === -1 || value.month === -1 || value.day === -1) {
                return;
            }
            if (!isValidDay(year, month, day)) {
                // 便宜上yearのエラーとして登録
                setError('year', { message: '正しい日付を入力してください。' });
            } else {
                clearErrors('year');
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const yearOptions = getBirthdayYearOptions();
    const monthOptions = getMonths();
    const dayOptions = getDays();

    return {
        control,
        errors,
        loading,
        showSnackbar,
        yearOptions,
        monthOptions,
        dayOptions,
        notCompletedProfile,
        setShowSnackbar,
        submit,
        getValues,
        setValue,
        watch,
    };
};
